import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { clsx } from 'clsx';
import { match, P } from 'ts-pattern';
import { useRealmAuthContext, useRealmAuthSetActiveAccess } from '@interxfi/api-client';
import type { WhoAmIContext } from '@interxfi/api-client/realm-auth/store';
import { type ActiveRealmContextType, TenantContext, useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { DropdownMenu } from '@interxfi/design-system/components/dropdown-menu/dropdown-menu';
import type { DropdownMenuItem } from '@interxfi/design-system/components/dropdown-menu/list-item/dropdown-menu-list-item';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { Logomark } from '@interxfi/design-system/components/logomark/logomark';
import { Tooltip, TooltipActivator } from '@interxfi/design-system/components/tooltip/tooltip';
import { Typography, type TypographyProps } from '@interxfi/design-system/components/typography';
import CompanyIcon from '@interxfi/design-system/icons/react/icon-company';
import DisclosureDownSmallIcon from '@interxfi/design-system/icons/react/icon-disclosure-down-small';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import SettingsIcon from '@interxfi/design-system/icons/react/icon-settings';
import { sortByKey, sortByPriority } from '@interxfi/utilities/sort';
import { O5_DEPLOYMENT_BASE_PATH } from '../../../../features/o5/deployment/o5-deployment-routing';
import { CreateProgramDrawer } from '../../../../features/userauth/program/create-program-drawer';
import { ProgramLogomark } from '../../../../features/userauth/program/logomark/program-logomark';
import { getProgramDetailPath } from '../../../../features/userauth/program/program-routing';
import { RealmPill } from '../../../realm/realm-pill';
import { useLayoutContext } from '../../context';
import { SwitchTenantDialog } from '../../switch-tenant-dialog/switch-tenant-dialog';
import styles from './context-menu.module.scss';

const realmPillTypographyOverrides = {
  size: '0.75rem',
} as const satisfies TypographyProps;

const clearingContextSortOrder = [
  `${TenantContext.Clearing}-simulated`,
  `${TenantContext.Clearing}-uat`,
  `${TenantContext.Clearing}-live`,
  TenantContext.Clearing,
  `${TenantContext.Program}-simulated`,
  `${TenantContext.Program}-uat`,
  `${TenantContext.Program}-live`,
  TenantContext.Program,
] as const satisfies string[];

function hasAssignedRole(access: WhoAmIContext['accesses'][number]): boolean {
  return Boolean(access.role);
}

function getClearingContextAccessMenuItems(
  activeAccess: ActiveRealmContextType | undefined,
  accesses: WhoAmIContext | undefined,
  accessGroup: string | undefined,
): DropdownMenuItem[] {
  const clearingContextAccesses = sortByPriority(
    sortByKey(
      (accesses?.accesses || []).filter(
        (access) =>
          hasAssignedRole(access) &&
          (access.tenant?.tenantType?.toLowerCase() === TenantContext.Program ||
            access.tenant?.tenantType?.toLowerCase() === TenantContext.Clearing) &&
          access.access?.realmAccessId !== activeAccess?.activeAccess?.realmAccessId,
      ),
      (access) => access.tenant?.data?.spec?.name || '',
    ),
    clearingContextSortOrder,
    (access) => {
      const tenantType = access.tenant?.tenantType || '';
      const merged = [tenantType, access.realm?.data?.spec?.metadata?.connectivity].filter(Boolean).join('-');
      if (clearingContextSortOrder.includes(merged as (typeof clearingContextSortOrder)[number])) {
        return merged;
      }

      return tenantType;
    },
  );

  return clearingContextAccesses.map(
    (access) =>
      ({
        id: access.access?.realmAccessId || `${access.tenant?.tenantId}-${access.tenant?.tenantType}-${access.realm?.realmId}`,
        group: accessGroup,
        icon: match(access)
          .with({ tenant: { tenantType: TenantContext.Clearing } }, () => (
            <Logomark size="small" tenantName="Clearing" tenantType={TenantContext.Clearing} />
          ))
          .otherwise(() => {
            return <ProgramLogomark programId={access.tenant?.tenantId} shouldLoadLogoMark size="small" withLink={false} />;
          }),
        label: match(access)
          .with({ tenant: { tenantType: TenantContext.Clearing } }, () => 'Interchange Clearing')
          .otherwise((a) => a?.tenant?.data?.spec?.name || 'Program'),
        pill: access.realm?.data.spec?.name ? (
          <RealmPill realmName={access.realm.data.spec.name} size="small" typographyProps={realmPillTypographyOverrides} />
        ) : undefined,
        dialog: match(access)
          .with({ realm: { realmId: P.not(P.nullish) }, tenant: { tenantId: P.not(P.nullish) } }, (a) => ({
            Component: SwitchTenantDialog,
            props: { targetRealmId: a.realm.realmId, targetTenantId: a.tenant.tenantId },
          }))
          .otherwise(() => undefined),
      }) as const satisfies DropdownMenuItem,
  );
}

function getClearingWorkspaceMenuItem(
  activeAccess: ActiveRealmContextType | undefined,
  accesses: WhoAmIContext | undefined,
  accessGroup?: string,
): DropdownMenuItem | undefined {
  const clearingContextAccesses = getClearingContextAccessMenuItems(activeAccess, accesses, accessGroup);

  if (!clearingContextAccesses.length) {
    return undefined;
  }

  // If they've got a clearing access, show the "Clearing Workspace" label, otherwise show "Program Workspace"
  return {
    ...clearingContextAccesses[0],
    label: accesses?.accesses?.find((access) => hasAssignedRole(access) && access.tenant?.tenantType?.toLowerCase() === TenantContext.Clearing)
      ? 'Clearing Workspace'
      : 'Program Workspace',
    pill: undefined,
  };
}

function getOrganizationAccesses(activeAccess: ActiveRealmContextType | undefined, accesses: WhoAmIContext | undefined) {
  return sortByKey(
    (accesses?.accesses || []).filter(
      (access) =>
        hasAssignedRole(access) &&
        access.tenant?.tenantType?.toLowerCase() === TenantContext.Organization &&
        access.access?.realmAccessId !== activeAccess?.activeAccess?.realmAccessId,
    ),
    (access) => access.tenant?.data?.spec?.name || '',
  );
}

function getOrganizationAccessMenuItems(
  setActiveAccess: ReturnType<typeof useRealmAuthSetActiveAccess>,
  activeAccess: ActiveRealmContextType | undefined,
  accesses: WhoAmIContext | undefined,
  accessGroup: string | undefined,
): DropdownMenuItem[] {
  const organizationAccesses = getOrganizationAccesses(activeAccess, accesses);

  return organizationAccesses.map(
    (access) =>
      ({
        id: access.access?.realmAccessId || `${access.tenant?.tenantId}-${access.tenant?.tenantType}-${access.realm?.realmId}`,
        group: accessGroup,
        icon: <Logomark size="small" tenantName={access?.tenant?.data?.spec?.name} tenantType={TenantContext.Organization} />,
        label: access?.tenant?.data?.spec?.name || 'Organization',
        LinkElement: Link,
        onClick: () => {
          if (access?.access?.realmAccessId) {
            setActiveAccess(access.access.realmAccessId);
          }
        },
        to: '/user',
        preload: false,
      }) as const satisfies DropdownMenuItem,
  );
}

function getOrganizationUserManagementMenuItem(
  setActiveAccess: ReturnType<typeof useRealmAuthSetActiveAccess>,
  activeAccess: ActiveRealmContextType | undefined,
  accesses: WhoAmIContext | undefined,
  accessGroup: string | undefined,
): DropdownMenuItem | undefined {
  const orgAccesses = getOrganizationAccesses(activeAccess, accesses);
  if (orgAccesses.length === 0) {
    return undefined;
  }

  const accessToUse = orgAccesses.find((access) => access.access?.data?.spec?.isAdmin) || orgAccesses[0];

  return {
    id: accessToUse.access?.realmAccessId || `${accessToUse.tenant?.tenantId}-${accessToUse.tenant?.tenantType}-${accessToUse.realm?.realmId}`,
    group: accessGroup,
    icon: <CompanyIcon aria-hidden />,
    label: 'Admin Workspace',
    LinkElement: Link,
    onClick: () => {
      if (accessToUse?.access?.realmAccessId) {
        setActiveAccess(accessToUse.access.realmAccessId);
      }
    },
    to: '/user',
    preload: false,
  } as const satisfies DropdownMenuItem;
}

function getTechOpsAccessMenuItem(
  setActiveAccess: ReturnType<typeof useRealmAuthSetActiveAccess>,
  activeAccess: ActiveRealmContextType | undefined,
  accesses: WhoAmIContext | undefined,
  group?: string,
): DropdownMenuItem | undefined {
  const techOpsAccess = accesses?.accesses?.find(
    (access) => hasAssignedRole(access) && access.tenant?.tenantType?.toLowerCase() === TenantContext.O5,
  );

  if (!techOpsAccess || activeAccess?.activeTenantContext === TenantContext.O5) {
    return undefined;
  }

  return {
    id: 'techOps',
    icon: <Logomark size="small" tenantName="TechOps" tenantType={TenantContext.O5} />,
    label: 'TechOps',
    LinkElement: Link,
    group,
    onClick: () => {
      if (techOpsAccess.access?.realmAccessId) {
        setActiveAccess(techOpsAccess.access.realmAccessId);
      }
    },
    to: O5_DEPLOYMENT_BASE_PATH,
    preload: false,
  };
}

function getPlatformAccessMenuItem(
  setActiveAccess: ReturnType<typeof useRealmAuthSetActiveAccess>,
  activeAccess: ActiveRealmContextType | undefined,
  accesses: WhoAmIContext | undefined,
): DropdownMenuItem | undefined {
  const platformAccess = accesses?.accesses?.find(
    (access) => hasAssignedRole(access) && access.tenant?.tenantType?.toLowerCase() === TenantContext.Platform,
  );

  if (!platformAccess || activeAccess?.activeTenantContext === TenantContext.Platform) {
    return undefined;
  }

  return {
    id: 'platform',
    icon: <Logomark size="small" tenantName="Access Management" tenantType={TenantContext.Platform} />,
    label: 'Platform Workspace',
    LinkElement: Link,
    onClick: () => {
      if (platformAccess.access?.realmAccessId) {
        setActiveAccess(platformAccess.access.realmAccessId);
      }
    },
    to: '/user',
    preload: false,
  };
}

function buildClearingCoreContextMenuItems(
  setActiveAccess: ReturnType<typeof useRealmAuthSetActiveAccess>,
  activeAccess: ActiveRealmContextType | undefined,
  accesses: WhoAmIContext | undefined,
  accessGroup: string | undefined,
): DropdownMenuItem[] {
  const programAndClearingAccesses = getClearingContextAccessMenuItems(activeAccess, accesses, accessGroup);
  const platformAccessItem = getPlatformAccessMenuItem(setActiveAccess, activeAccess, accesses);
  const techOpsAccessItem = getTechOpsAccessMenuItem(setActiveAccess, activeAccess, accesses);
  const organizationAccessItem = getOrganizationUserManagementMenuItem(setActiveAccess, activeAccess, accesses, undefined);
  const { activeTenant, activeTenantContext } = activeAccess || {};
  const hasOrganizationAdmin = Boolean(accesses?.organizationMemberships?.find((m) => m.organizationMember?.data?.permissions?.isAdmin));
  const canCreateProgram = accesses?.accesses.some((a) => a.tenant?.tenantType === TenantContext.Platform || hasOrganizationAdmin);

  return [
    ...(activeTenant?.tenantId && activeTenantContext === TenantContext.Program
      ? [
          {
            id: 'programSettings',
            icon: <SettingsIcon aria-hidden />,
            label: 'Program Settings',
            LinkElement: Link,
            to: getProgramDetailPath(activeTenant.tenantId),
            preload: false,
          },
        ]
      : []),
    ...(platformAccessItem ? [platformAccessItem] : organizationAccessItem ? [organizationAccessItem] : []),
    ...(techOpsAccessItem ? [techOpsAccessItem] : []),
    ...(canCreateProgram
      ? [
          {
            id: 'addProgram',
            group: accessGroup,
            icon: <PlusIcon aria-hidden />,
            label: 'Add a Program',
            interaction: { type: 'drawer', drawer: { Component: CreateProgramDrawer, props: {} } },
          } as const satisfies DropdownMenuItem,
        ]
      : []),
    ...programAndClearingAccesses,
  ];
}

function buildPlatformContextMenuItems(
  setActiveAccess: ReturnType<typeof useRealmAuthSetActiveAccess>,
  activeAccess: ActiveRealmContextType | undefined,
  accesses: WhoAmIContext | undefined,
): DropdownMenuItem[] {
  const organizationAccesses = getOrganizationAccessMenuItems(setActiveAccess, activeAccess, accesses, undefined);
  const clearingWorkspaceAccess = getClearingWorkspaceMenuItem(activeAccess, accesses, 'access');
  const platformAccessItem = getPlatformAccessMenuItem(setActiveAccess, activeAccess, accesses);
  const techOpsAccessItem = getTechOpsAccessMenuItem(setActiveAccess, activeAccess, accesses, 'access');

  return [
    ...(platformAccessItem ? [platformAccessItem] : []),
    ...organizationAccesses,
    ...(clearingWorkspaceAccess ? [clearingWorkspaceAccess] : []),
    ...(techOpsAccessItem ? [techOpsAccessItem] : []),
  ];
}

function buildTechOpsContextMenuItems(
  setActiveAccess: ReturnType<typeof useRealmAuthSetActiveAccess>,
  activeAccess: ActiveRealmContextType | undefined,
  accesses: WhoAmIContext | undefined,
): DropdownMenuItem[] {
  const clearingWorkspaceAccess = getClearingWorkspaceMenuItem(activeAccess, accesses);
  const platformAccessItem = getPlatformAccessMenuItem(setActiveAccess, activeAccess, accesses);
  const organizationAccessItem = getOrganizationUserManagementMenuItem(setActiveAccess, activeAccess, accesses, undefined);

  return [
    ...(clearingWorkspaceAccess ? [clearingWorkspaceAccess] : []),
    ...(platformAccessItem ? [platformAccessItem] : organizationAccessItem ? [organizationAccessItem] : []),
  ];
}

function buildContextMenuItems(
  setActiveAccess: ReturnType<typeof useRealmAuthSetActiveAccess>,
  activeAccess: ActiveRealmContextType | undefined,
  accesses: WhoAmIContext | undefined,
): DropdownMenuItem[] {
  const { activeTenantContext } = activeAccess || {};

  switch (activeTenantContext) {
    case TenantContext.Platform:
    case TenantContext.Organization:
      return buildPlatformContextMenuItems(setActiveAccess, activeAccess, accesses);
    case TenantContext.O5:
      return buildTechOpsContextMenuItems(setActiveAccess, activeAccess, accesses);
    case TenantContext.Clearing:
    case TenantContext.Program:
    default:
      return buildClearingCoreContextMenuItems(setActiveAccess, activeAccess, accesses, 'access');
  }
}

export function ContextMenu() {
  const { collapsedSidebarStatus, showCollapsedSidebar } = useLayoutContext();
  const setActiveRealmAccess = useRealmAuthSetActiveAccess();
  const access = useActiveRealmAPIContext();
  const accesses = useRealmAuthContext();
  const tenantOptions = useMemo(() => buildContextMenuItems(setActiveRealmAccess, access, accesses), [setActiveRealmAccess, access, accesses]);
  const tenantName = match(access?.activeTenantContext)
    .with(TenantContext.Clearing, () => 'Interchange Clearing')
    .with(TenantContext.O5, () => 'TechOps')
    .with(TenantContext.Platform, () => 'Platform Workspace')
    .otherwise(() => access?.activeTenant?.data?.spec?.name);
  const DropdownActivator = showCollapsedSidebar ? TooltipActivator : 'button';

  const base = (
    <DropdownMenu
      activator={
        <DropdownActivator className={styles.contextMenuActivator} type="button">
          <HorizontalStack
            align="space-between"
            blockAlign="center"
            className={clsx(styles.contextMenu, showCollapsedSidebar && styles.isCollapsed, styles[`state-${collapsedSidebarStatus}`])}
            gap={2}
            wrap="nowrap"
          >
            <HorizontalStack blockAlign="center" fullWidth gap={2} wrap="nowrap">
              <Logomark size="large" tenantName={tenantName} tenantType={access?.activeTenantContext} />

              {!showCollapsedSidebar ? (
                <Typography className={clsx(styles.labelContainer, styles.colorElement)} lineHeight="1rem" size="0.875rem" wrap="ellipsis">
                  {tenantName || <Skeleton fillParent />}
                </Typography>
              ) : null}
            </HorizontalStack>

            {!showCollapsedSidebar && tenantOptions?.length ? (
              <DisclosureDownSmallIcon aria-hidden className={clsx(styles.labelContainer, styles.colorElement, styles.disclosureIcon)} width={16} />
            ) : null}
          </HorizontalStack>
        </DropdownActivator>
      }
      align={showCollapsedSidebar ? 'start' : 'center'}
      alignOffset={showCollapsedSidebar ? 16 : undefined}
      ellipsizeOverflow
      groupStyle="border"
      items={tenantOptions}
      label="Context menu"
      labelIsHidden
      matchActivatorWidth={false}
      popoverClassName={styles.popover}
      sideOffset={0}
    />
  );

  if (showCollapsedSidebar) {
    return (
      <Tooltip activator={base} activatorIsPrimitive side="right" sideOffset={-8} withArrow={false}>
        {tenantName}
      </Tooltip>
    );
  }

  return base;
}
